import { FC, useRef, useState } from "react";
import { useChatStore } from "../../../stores/ChatStore";
import useAccessToken from "../../../hooks/AccessToken";
import { useIntl } from "react-intl";
import { KTSVG } from "../../../_metronic/helpers";
import Swal from "sweetalert2";
import { putEditChat } from "../../../api/ChatCRUD";
import { chatMode } from "../../../functions/chat";
import { useSettingsStore } from "../../../stores/SettingsStore";
import { ChatMessage } from "../../../interfaces/Chat/Chat";
import { Message } from "../../../interfaces/Chat/Message";

interface Props {
    chatMessage: ChatMessage
}

const UpdateMessageBubble: FC<Props> = ({ chatMessage }) => {
    const token = useAccessToken()
    const intl = useIntl()
    const { settings } = useSettingsStore()
    const closeModalRef = useRef<HTMLButtonElement>(null);

    const {
        phoneNumber, editMessage,
        tokenChat, message, setEditMessage
    } = useChatStore()

    const [textareaRows, setTextareaRows] = useState(1);

    const modalRef = useRef<HTMLDivElement | null>(null);
    const chatPhone = phoneNumber?.split('-')[0] || ''
    const chatAs = phoneNumber?.split('-')[1] || ''

    const adjustTextareaRows = (text: string) => {
        const lineCount = text.split("\n").length;
        setTextareaRows(Math.min(6, Math.max(1, lineCount))); // limit rows between 1 and 6
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const inputText = e.target.value;
        setEditMessage(inputText);
        adjustTextareaRows(inputText); // Adjust rows as user types
    };

    const handleEditChat = async (receiver_phone: string, as_phone: string, messageId: string, new_message: string) => {
        if (!new_message) {
            Swal.fire({
                icon: 'error',
                title: intl.formatMessage({ id: "ALERT.TITLE.EMPTY_MESSAGE" }),
                confirmButtonText: 'Ok',
                heightAuto: false,
            });

            return
        }

        Swal.fire({
            title: 'Loading',
            html: intl.formatMessage({ id: "ALERT.TITLE.EDITING_MESSAGE" }),

            didOpen: () => {
                Swal.showLoading()
            },
        })

        try {
            const response = await putEditChat(messageId, receiver_phone, as_phone, new_message, chatMode(settings.chat_version), token, tokenChat);
            if (response.data.success) {
                Swal.fire({
                    icon: 'success',
                    title: intl.formatMessage({ id: "ALERT.TITLE.MESSAGE_EDITED" }),
                })
                closeModalRef.current?.click();
            }
        } catch (error: any) {
            Swal.fire({
                icon: 'error',
                title: error.response.data.message,
                confirmButtonText: 'Ok',
                heightAuto: false,
            });
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>, editedMessage?: ChatMessage) => {
        // Check if Ctrl key is pressed and the key is 'b'
        if ((e.ctrlKey && e.key.toLowerCase() === 'b') || (e.metaKey && e.key.toLowerCase() === 'b')) {
            // Prevent the default behavior of the browser
            e.preventDefault();

            // Get the selected text
            const selectedText = window.getSelection()?.toString();

            // If there is selected text, replace the selected text with '*'
            if (selectedText) {
                const updatedMessage = message.replace(selectedText, `*${selectedText}*`);
                setEditMessage(updatedMessage);
            } else {
                // If no text is selected, insert '*' at the current cursor position
                const cursorPosition = e.currentTarget.selectionStart || 0;
                const newMessage = message.slice(0, cursorPosition) + '**' + message.slice(cursorPosition);
                setEditMessage(newMessage);

                // Adjust the cursor position after inserting '*'
                e.currentTarget.setSelectionRange(cursorPosition + 2, cursorPosition + 2);
            }
        } else if ((e.ctrlKey && e.key.toLowerCase() === 'i') || (e.metaKey && e.key.toLowerCase() === 'i')) {
            // Prevent the default behavior of the browser
            e.preventDefault();

            // Get the selected text
            const selectedText = window.getSelection()?.toString();

            // If there is selected text, replace the selected text with '_'
            if (selectedText) {
                const updatedMessage = message.replace(selectedText, `_${selectedText}_`);
                setEditMessage(updatedMessage);
            } else {
                // If no text is selected, insert '_' at the current cursor position
                const cursorPosition = e.currentTarget.selectionStart || 0;
                const newMessage = message.slice(0, cursorPosition) + '__' + message.slice(cursorPosition);
                setEditMessage(newMessage);

                // Adjust the cursor position after inserting '_*'
                e.currentTarget.setSelectionRange(cursorPosition + 2, cursorPosition + 2);
            }
        }

        // Call your existing onKeyDown logic if needed
        if (editedMessage) {
            onEnterPress(e, editedMessage);
        } else {
            onEnterPress(e);
        }
    };

    const onEnterPress = (e: React.KeyboardEvent<HTMLTextAreaElement>, editedMessage?: ChatMessage) => {
        if (e.keyCode === 13 && e.shiftKey === false) {
            e.preventDefault()
            if (editedMessage) {
                handleEditChat(chatPhone, chatAs, editedMessage.id!, editMessage)
            }
        }
    }

    return (
        <div className="modal fade" tabIndex={-1} id="update-message-bubble-modal" ref={modalRef}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">{intl.formatMessage({ id: 'FORM.ACTION.UPDATE' })} {intl.formatMessage({ id: 'FORM.LABEL.MESSAGE' })}</h5>
                        <div
                            className="btn btn-icon btn-sm btn-active-light-primary ms-2"
                            data-bs-dismiss="modal"
                        >
                            <KTSVG
                                path="/media/icons/duotune/arrows/arr061.svg"
                                className="svg-icon svg-icon-2x"
                            />
                        </div>
                    </div>
                    <div className="modal-body">
                        <textarea
                            style={{ resize: 'none', height: '70px', maxHeight: '200px', overflowY: 'auto' }}
                            id='chat-input'
                            className='form-control form-control-lg txt-chat'
                            data-kt-element='input'
                            rows={textareaRows}
                            placeholder='Type a message'
                            value={editMessage}
                            onChange={handleInputChange}
                            onKeyDown={(e) => { handleKeyDown(e, chatMessage) }}
                        ></textarea>
                    </div>
                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-light"
                            data-bs-dismiss="modal"
                            ref={closeModalRef}
                        >
                            {intl.formatMessage({ id: "FORM.ACTION.CANCEL" })}
                        </button>
                        <button type="button" className={`btn btn-primary me-10`} onClick={() => handleEditChat(chatPhone, chatAs, chatMessage.id!, editMessage)}>
                            <span className="indicator-label">
                                {intl.formatMessage({ id: 'FORM.ACTION.UPDATE' })}
                            </span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export { UpdateMessageBubble }